// Session state for the whole app: who is signed in (GET /auth/me), their roles, and the CSRF token.
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, type ReactNode, useContext, useEffect, useMemo } from 'react'
import { api, setCsrfToken } from './api'
import { type Me, parseLogoutUrl, parseMe } from './auth'
import { paths } from './endpoints'
import type { Viewer } from './permissions'

export const meQueryOptions = queryOptions({
  queryKey: ['me'],
  queryFn: async ({ signal }) => {
    const me = parseMe(await api.get(paths.me, { skipAuthRedirect: true, signal }))
    setCsrfToken(me.csrfToken)
    return me
  },
  staleTime: 5 * 60_000,
  retry: false,
})

interface AuthState {
  me: Me
  viewer: Viewer
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  // The route guard (app/route-base.ts) has already loaded the session before this renders.
  const { data: me } = useQuery(meQueryOptions)

  useEffect(() => {
    if (me) setCsrfToken(me.csrfToken)
  }, [me])

  const value = useMemo<AuthState | null>(() => {
    if (!me) return null
    return {
      me,
      viewer: { roles: me.roles, isGuest: me.isGuest },
      signOut: async () => {
        let target = '/login?error=signed_out'
        try {
          target = parseLogoutUrl(await api.post(paths.logout, {}, { skipAuthRedirect: true }))
        } catch {
          // Session may already be gone; still clear local state and leave.
        }
        setCsrfToken(null)
        qc.clear()
        window.location.assign(target)
      },
    }
  }, [me, qc])

  if (!value) return null
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider> (the signed-in app layout)')
  return ctx
}

/** For components that may render outside the signed-in layout. */
export function useOptionalAuth(): AuthState | null {
  return useContext(AuthContext)
}
